import BButton from 'components/common/BButton';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent, useEffect, useState } from 'react';
import { useFilterStore } from 'states/useFilterStore';
import VehicleListFilterSection from './Filter/VehicleListFilterSection';
import BCheckbox from 'components/Form/BCheckbox';
import BRadio from 'components/Form/BRadio';
import { useForm } from 'hooks/useForm';
import { FilterState } from 'types/search.type';
import { VehicleType } from 'enums/vehicleTypes';
import Modal from 'components/common/Modal';
import { Sliders } from '@phosphor-icons/react';
import BNumberInput from 'components/Form/BNumberInput';
import BLabelWrapper from 'components/Form/BLabelWrapper';
import { useRouter } from 'next/router';
import { searchService } from 'services/SearchService';
import router from 'next/router';
import queryString from 'query-string';
import { SearchQueryKey } from 'enums/searchQueryKey';

interface VehicleListFilterProps {
  defaultVehicleType?: VehicleType;
}

const VehicleListFilter: FunctionComponent<VehicleListFilterProps> = ({
  defaultVehicleType,
}) => {
  const { t } = useTranslation('search');
  const { asPath } = useRouter();
  const [show, setShow] = useState<boolean>(false);
  const [isOptionsFetched, setIsOptionsFetched] = useState<boolean>(false);
  const {
    filterOptions,
    getFilterOptions,
    setFilterParams,
    cleanFilterParams,
  } = useFilterStore();

  const defaultState: FilterState = {
    onlyImmediatelyReservable: false,
    onlySuitableForWinter: false,
    canReserveForOneHour: false,
    rating: 0,
    vehicleTypes: defaultVehicleType ? [defaultVehicleType] : [],
    usageConditions: [],
    facilities: [],
    priceRange: {
      min: filterOptions?.priceRange.min || 0,
      max: filterOptions?.priceRange.max || 0,
    },
  };

  const [state, dispatch] = useForm<FilterState>(defaultState);

  const setFiltersFromQuery = async () => {
    const queryObj = router.asPath.split('?')[1];
    const filterQueryParams = searchService.getFilterQueryParams(queryObj);
    let filterOpts = filterOptions;
    if (!isOptionsFetched || filterOptions !== null) {
      filterOpts = await getFilterOptions();
      setIsOptionsFetched(true);
    }
    if (filterOpts) {
      dispatch({
        type: 'REPLACE_STATE',
        payload: {
          onlyImmediatelyReservable:
            !!filterQueryParams.onlyImmediatelyReservable,
          canReserveForOneHour: !!filterQueryParams.canReserveForOneHour,
          rating: filterQueryParams.rating || defaultState.rating,
          vehicleTypes:
            filterQueryParams.vehicleTypes || defaultState.vehicleTypes,
          usageConditions:
            filterQueryParams.usageConditions || defaultState.usageConditions,
          facilities: filterQueryParams.facilities || defaultState.facilities,
          onlySuitableForWinter: !!filterQueryParams.onlySuitableForWinter,
          priceRange: {
            min: filterQueryParams.priceRange.min || filterOpts.priceRange.min,
            max: filterQueryParams.priceRange.max || filterOpts.priceRange.max,
          },
        },
      });
    }
  };

  useEffect(() => {
    setFiltersFromQuery();
  }, [asPath]);

  const hasMinRentalDurationOneHour =
    (filterOptions?.minimumRentDurationRange.min ?? 0) <= 1;

  const ratingOptions = [4.5, 4, 3, 2, 1];

  const setQueryParam = () => {
    let path = router.asPath;
    const allQueries = path.split('?')[1];
    const mappedQueries = queryString.parse(allQueries);
    const searchQueryKeys = Object.values(SearchQueryKey);
    const onlySearchQueries = Object.keys(mappedQueries).reduce((acc, key) => {
      if (searchQueryKeys.includes(key as SearchQueryKey)) {
        return {
          ...acc,
          [key]: mappedQueries[key],
        };
      }
      return acc;
    }, {});
    const searchQs = queryString.stringify(onlySearchQueries);
    const qs = searchService.formatFiltersToQueryParamString(state);
    path = path.split('?')[0] + '?';
    path += searchQs + (searchQs ? '&' : '');
    path += qs;
    router.replace(path);
  };

  const onSave = () => {
    setFilterParams(state);
    setShow(false);
    setQueryParam();
  };

  const onClear = () => {
    cleanFilterParams();
    dispatch({
      payload: defaultState,
      type: 'REPLACE_STATE',
    });
    setShow(false);
  };

  const activeFilters = isOptionsFetched
    ? Object.entries(defaultState).reduce((acc, item) => {
        return JSON.stringify(item[1]) !==
          JSON.stringify(state[item[0] as keyof FilterState])
          ? acc + 1
          : acc;
      }, 0)
    : 0;

  return (
    <div>
      <BButton onClick={() => setShow(true)}>
        <div className="flex items-center gap-2">
          <Sliders size={24} />
          <span className="hidden md:block">{t('filter.title')}</span>
          {activeFilters > 0 ? <span>({activeFilters})</span> : null}
        </div>
      </BButton>
      <Modal
        show={show}
        setShow={setShow}
        modalTitle={t('filter.title')}
        removeBottomMargin
        removeSlideToClose
      >
        <div>
          <div className="space-y-6 overflow-auto pr-3 pb-3 md:h-[70vh]">
            <VehicleListFilterSection>
              <div className="space-y-3">
                <BCheckbox
                  checked={state.onlyImmediatelyReservable}
                  name="immediately-reserve"
                  label={t('filter.onlyImmediatelyReservable.label')}
                  onChange={(e) =>
                    dispatch({
                      payload: e.target.checked,
                      type: 'onlyImmediatelyReservable',
                    })
                  }
                />
                <BCheckbox
                  checked={state.onlySuitableForWinter}
                  name="winter-suitable"
                  label={t('filter.isWinterSuitable.label')}
                  onChange={(e) =>
                    dispatch({
                      payload: e.target.checked,
                      type: 'onlySuitableForWinter',
                    })
                  }
                />
                {hasMinRentalDurationOneHour ? (
                  <BCheckbox
                    checked={state.canReserveForOneHour}
                    name="min-rental-duration"
                    label={t('filter.oneHourRentalVehicles.label')}
                    onChange={(e) =>
                      dispatch({
                        payload: e.target.checked,
                        type: 'canReserveForOneHour',
                      })
                    }
                  />
                ) : null}
              </div>
            </VehicleListFilterSection>
            <VehicleListFilterSection title={t('filter.vehicleType.title')}>
              {filterOptions?.vehicleTypes.map((vt) => (
                <BCheckbox
                  key={vt.toString()}
                  value={vt}
                  checked={state.vehicleTypes.includes(vt as VehicleType)}
                  label={t(`common:boatTypes.${vt}`)}
                  className="mt-2"
                  name="vehicle-type"
                  onChange={(e) =>
                    dispatch({
                      payload: e.target.value as unknown as VehicleType[],
                      type: 'vehicleTypes',
                      isAppend: e.target.checked,
                    })
                  }
                />
              ))}
            </VehicleListFilterSection>
            <VehicleListFilterSection title={t('filter.priceRange.title')}>
              <div className="mt-1 flex justify-between gap-2">
                <BLabelWrapper label={t('common:multiSliderPlaceholders.min')}>
                  <BNumberInput
                    value={state.priceRange.min}
                    handleChange={(e) => {
                      dispatch({
                        payload: {
                          min: +e,
                          max: state.priceRange.max,
                        },
                        type: 'priceRange',
                      });
                    }}
                  />
                </BLabelWrapper>
                <BLabelWrapper label={t('common:multiSliderPlaceholders.max')}>
                  <BNumberInput
                    value={state.priceRange.max}
                    handleChange={(e) => {
                      dispatch({
                        payload: {
                          min: state.priceRange.min,
                          max: +e,
                        },
                        type: 'priceRange',
                      });
                    }}
                    placeholder={t('common:multiSliderPlaceholders.max')}
                  />
                </BLabelWrapper>
              </div>
            </VehicleListFilterSection>
            <VehicleListFilterSection
              title={t('filter.rating.title')}
              titleSide={
                state.rating ? (
                  <BButton
                    className="text-sm underline hover:no-underline"
                    priority="none"
                    onClick={() => {
                      dispatch({
                        payload: 0,
                        type: 'rating',
                      });
                    }}
                  >
                    {t('filter.rating.sideTitle')}
                  </BButton>
                ) : null
              }
            >
              {ratingOptions.map((ro) => (
                <BRadio
                  key={ro.toString()}
                  value={ro}
                  checked={state.rating === ro}
                  label={t('filter.rating.optionsLabel', { count: ro })}
                  className="mb-2"
                  name="rating"
                  onChange={(e) =>
                    dispatch({
                      payload: +e.target.value,
                      type: 'rating',
                    })
                  }
                />
              ))}
            </VehicleListFilterSection>
            <VehicleListFilterSection title={t('filter.usageConditions.title')}>
              <div className="space-y-2">
                {filterOptions?.usageConditions.map((uc) => (
                  <BCheckbox
                    key={uc.value}
                    label={uc.label}
                    value={uc.value}
                    checked={state.usageConditions.includes(uc.value)}
                    name="usage-condition"
                    onChange={(e) =>
                      dispatch({
                        payload: +e.target.value,
                        type: 'usageConditions',
                        isAppend: e.target.checked,
                      })
                    }
                  />
                ))}
              </div>
            </VehicleListFilterSection>
            <VehicleListFilterSection title={t('filter.facilities.title')}>
              <div className="space-y-2">
                {filterOptions?.vehicleFacilities.map((vf) => (
                  <BCheckbox
                    key={vf.value}
                    label={vf.label}
                    value={vf.value}
                    checked={state.facilities.includes(vf.value)}
                    name="facilities"
                    onChange={(e) =>
                      dispatch({
                        payload: +e.target.value,
                        type: 'facilities',
                        isAppend: e.target.checked,
                      })
                    }
                  />
                ))}
              </div>
            </VehicleListFilterSection>
          </div>
          <div className="mb-[env(safe-area-inset-bottom, 0px)] sticky bottom-0 left-0 w-full bg-white py-2">
            <div className="fading-bottom after:-top-2" />
            <div className="flex items-center justify-between gap-4">
              <BButton
                variant="secondary"
                priority="outlined"
                onClick={onClear}
              >
                {t('filter.actions.clear')}
              </BButton>
              <BButton onClick={onSave}>{t('filter.actions.save')}</BButton>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VehicleListFilter;
